import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Mail, Send, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ALERT_LABELS = {
  new_order: { title: 'New Orders', desc: 'Instant alert when a customer completes a paid order.' },
  payment_failed: { title: 'Payment Failed / Pending', desc: 'Instant alert when a Razorpay payment fails or is pending.' },
  abandoned_checkout: { title: 'Abandoned Checkout', desc: 'Sent ~delay minutes after a shopper enters contact info on checkout but doesn\'t pay.' },
  abandoned_cart: { title: 'Abandoned Cart', desc: 'Sent ~delay minutes after a shopper adds items but doesn\'t reach checkout.' },
  daily_summary: { title: 'Daily Summary', desc: 'Once a day at ~09:30 IST — visitors, product views, ATC, WhatsApp clicks and top products.' },
};

export const EmailAlertsPanel = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [receiverDraft, setReceiverDraft] = useState('');
  const [delayDraft, setDelayDraft] = useState(45);

  const token = () => localStorage.getItem('adminToken');

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/email-settings`, { headers: { Authorization: `Bearer ${token()}` } });
      setSettings(r.data);
      setReceiverDraft(r.data?.receiver_email || '');
      setDelayDraft(r.data?.abandoned_delay_minutes || 45);
    } catch (e) {
      toast.error('Failed to load email settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSettings(); }, []);

  const persist = async (patch) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/email-settings`, patch, { headers: { Authorization: `Bearer ${token()}` } });
      setSettings(r.data);
      toast.success('Settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleAlert = (key, value) => {
    if (!settings) return;
    setSettings({ ...settings, alerts: { ...settings.alerts, [key]: value } });
    persist({ alerts: { [key]: value } });
  };

  const saveReceiver = () => {
    const email = receiverDraft.trim();
    if (!email || !/.+@.+\..+/.test(email)) { toast.error('Enter a valid email'); return; }
    persist({ receiver_email: email });
  };

  const saveDelay = () => {
    const v = parseInt(delayDraft, 10);
    if (Number.isNaN(v) || v < 15 || v > 180) { toast.error('Delay must be 15-180 minutes'); return; }
    persist({ abandoned_delay_minutes: v });
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      await axios.post(`${API}/admin/email-settings/test`, {}, { headers: { Authorization: `Bearer ${token()}` } });
      toast.success(`Test email sent to ${settings?.receiver_email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test email failed — check SendGrid');
    } finally {
      setTesting(false);
    }
  };

  if (loading || !settings) {
    return <Card className="bg-white border-none shadow-lg p-6"><p className="text-sm text-[#8B7D6B]">Loading…</p></Card>;
  }

  return (
    <Card className="bg-white border-none shadow-lg p-6" data-testid="email-alerts-panel">
      <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
        <div>
          <h2 className="text-2xl font-seasons text-[#2C2C2C]">Email Alerts</h2>
          <p className="text-sm font-montserrat text-[#8B7D6B]">
            Lightweight notifications. Instant for orders / payment / abandoned. One daily summary for views & clicks.
          </p>
        </div>
        <Button onClick={sendTest} disabled={testing || saving} data-testid="email-test-btn" className="bg-[#6B4E37] hover:bg-[#5A4030] text-white">
          {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
          Send Test Email
        </Button>
      </div>

      {/* Receiver email */}
      <div className="bg-[#FAF6EE] border border-[#D4C4B0] rounded-lg p-4 mb-5">
        <label className="block text-xs font-montserrat text-[#6B4E37] uppercase tracking-wider mb-2">
          Receiver email
        </label>
        <div className="flex gap-2 flex-wrap">
          <Input
            type="email"
            value={receiverDraft}
            onChange={(e) => setReceiverDraft(e.target.value)}
            className="bg-white border-[#E8E2D5] max-w-md"
            data-testid="email-receiver-input"
          />
          <Button onClick={saveReceiver} disabled={saving} className="bg-[#C4B5A0] hover:bg-[#B0A090] text-white" data-testid="email-receiver-save">
            <Mail className="h-4 w-4 mr-2" /> Save
          </Button>
        </div>
        <p className="text-xs font-montserrat text-[#8B7D6B] mt-2">
          All alert emails are delivered to this address.
        </p>
      </div>

      {/* Abandoned delay */}
      <div className="bg-[#FAF6EE] border border-[#D4C4B0] rounded-lg p-4 mb-5">
        <label className="block text-xs font-montserrat text-[#6B4E37] uppercase tracking-wider mb-2">
          Abandoned cart / checkout delay (minutes)
        </label>
        <div className="flex gap-2 items-center flex-wrap">
          <Input
            type="number"
            min={15}
            max={180}
            value={delayDraft}
            onChange={(e) => setDelayDraft(e.target.value)}
            className="bg-white border-[#E8E2D5] w-32"
            data-testid="email-delay-input"
          />
          <Button onClick={saveDelay} disabled={saving} className="bg-[#C4B5A0] hover:bg-[#B0A090] text-white" data-testid="email-delay-save">
            Save
          </Button>
          <span className="text-xs font-montserrat text-[#8B7D6B]">Recommended: 30–60. Watcher scans every 5 min.</span>
        </div>
      </div>

      {/* Alert toggles */}
      <div className="space-y-2">
        {Object.entries(ALERT_LABELS).map(([key, info]) => (
          <div
            key={key}
            className="flex items-start justify-between gap-4 p-3 rounded border border-[#F0EDE5] hover:border-[#D4C4B0] transition"
            data-testid={`email-alert-row-${key}`}
          >
            <div className="min-w-0">
              <p className="font-montserrat text-sm text-[#2C2C2C]">{info.title}</p>
              <p className="font-montserrat text-xs text-[#8B7D6B] mt-0.5">{info.desc}</p>
            </div>
            <label className="cursor-pointer flex items-center gap-2 shrink-0">
              <input
                type="checkbox"
                checked={!!settings.alerts[key]}
                onChange={(e) => toggleAlert(key, e.target.checked)}
                className="sr-only peer"
                data-testid={`email-alert-toggle-${key}`}
              />
              <span className="relative inline-block w-10 h-5 rounded-full bg-[#E8E5E0] peer-checked:bg-green-500 transition">
                <span className="absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow transition peer-checked:translate-x-5" />
              </span>
              <span className="text-xs font-montserrat text-[#6B4E37] w-12">
                {settings.alerts[key] ? 'ON' : 'OFF'}
              </span>
            </label>
          </div>
        ))}
      </div>

      <p className="text-[11px] font-montserrat text-[#8B7D6B] mt-5">
        Tip: emails are sent via SendGrid using the `SENDGRID_API_KEY` configured in your backend `.env`.
        If the test email fails with 401, the key has expired — generate a fresh one and update `.env`.
      </p>
    </Card>
  );
};
